/**
 * Fetches and verifies the signed release manifest this page links from —
 * `version.json` and `version.json.sig`, served by `ipfs.gibs.finance`. Uses
 * the exact Ed25519 scheme and schema the publish pipeline and the redirect
 * Worker both use, so this page can never show a link the signed list does
 * not actually contain. See `docs/ipfs-releases.md` for the full contract.
 *
 * `sign.ts` and `schema.ts` are imported directly, not copied — both hold no
 * `node:*` import and use only `crypto.subtle`, so they run unchanged here.
 */
import {
  signatureFileSchema,
  type VersionManifest,
  versionManifestSchema,
} from '../../../scripts/ipfs-release/schema'
import { verifyManifestSignature } from '../../../scripts/ipfs-release/sign'

/** Why a fetched copy of the manifest could not be trusted. */
export type VerificationFailureReason = 'bad-signature' | 'invalid'

/**
 * The three outcomes this page renders directly. `unreachable` means a
 * fetch itself failed — no bytes came back at all. `verification-failed`
 * means bytes came back but do not verify or do not match the schema.
 * `verified` means the manifest is safe to read links from.
 */
export type VerificationOutcome =
  | { status: 'verified'; manifest: VersionManifest }
  | { status: 'verification-failed'; reason: VerificationFailureReason }
  | { status: 'unreachable' }

const decodeJson = (bytes: ArrayBuffer): unknown => JSON.parse(new TextDecoder().decode(bytes))

const fetchBytes = async (url: string, fetchImpl: typeof fetch): Promise<ArrayBuffer | null> => {
  try {
    const response = await fetchImpl(url, { cache: 'no-store' })
    if (!response.ok) {
      return null
    }
    return await response.arrayBuffer()
  } catch {
    return null
  }
}

/**
 * Fetches `manifestUrl` and `signatureUrl`, verifies the signature and the
 * schema, and returns the outcome the page renders from.
 *
 * `fetchImpl` defaults to the global `fetch` and exists so a test — or the
 * developer fixture harness (`src/harness.ts`) — can point this at anything
 * without touching a real network.
 */
export const verifyRelease = async (
  manifestUrl: string,
  signatureUrl: string,
  publicKeysByKeyId: Record<string, string>,
  fetchImpl: typeof fetch = fetch,
): Promise<VerificationOutcome> => {
  const [manifestBytes, signatureBytes] = await Promise.all([
    fetchBytes(manifestUrl, fetchImpl),
    fetchBytes(signatureUrl, fetchImpl),
  ])
  if (manifestBytes === null || signatureBytes === null) {
    return { status: 'unreachable' }
  }

  let signatureFile: { keyId: string; signature: string }
  try {
    signatureFile = signatureFileSchema.parse(decodeJson(signatureBytes))
  } catch {
    return { status: 'verification-failed', reason: 'invalid' }
  }

  const signatureIsValid = await verifyManifestSignature({
    manifestBytes,
    keyId: signatureFile.keyId,
    signatureBase64: signatureFile.signature,
    publicKeysByKeyId,
  })
  if (!signatureIsValid) {
    return { status: 'verification-failed', reason: 'bad-signature' }
  }

  let parsedManifest: unknown
  try {
    parsedManifest = decodeJson(manifestBytes)
  } catch {
    return { status: 'verification-failed', reason: 'invalid' }
  }

  const schemaResult = versionManifestSchema.safeParse(parsedManifest)
  if (!schemaResult.success) {
    return { status: 'verification-failed', reason: 'invalid' }
  }

  return { status: 'verified', manifest: schemaResult.data }
}

/** A human sentence for a verification failure — never shown alongside a link. */
export const verificationFailureText = (reason: VerificationFailureReason): string => {
  switch (reason) {
    case 'bad-signature':
      return 'The release list failed its signature check. The list may be tampered, or the signing key may have changed.'
    case 'invalid':
      return 'The release list does not match the expected format.'
    default:
      return reason satisfies never
  }
}
